import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import React, { useState } from "react";
import { LoadingSmall } from "@/components/LoadingSmall";
import { useTranslation } from "react-i18next";
import { PlayerTimezoneSelect } from "./PlayerTimezoneSelect";
import { PlayerFirmLicenceSelect } from "./PlayerFirmLicenceSelect";
import { PlayerMediaSelect } from "./PlayerMediaSelect";
import { NestedAreaSelector } from "@/components/NestedAreaSelector";
import { ScreenSizeSelect } from "@/pages/screenSize/ScreenSizeSelect";
import { getScreenSize } from "@/sideEffects/api";
import { useQuery } from "@tanstack/react-query";
import { selectActiveFirm } from "@/store/appSelectors";
import { useSelector } from "react-redux";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { MapIcon, MonitorIcon, LayoutIcon, GlobeIcon, ImageIcon, MapPinIcon } from "lucide-react";
import { LocationMapPicker } from "@/components/LocationMapPicker";
import { LoadingOverlay } from "@/components/LoadingOverlay";

const formSchema = z.object({
  name: z.string().min(1),
  screenSizeId: z.number().min(1),
  timeZone: z.string().min(1),
  firmLicenceintId: z.number(),
  channelId: z.number().optional().or(z.null()),
  contentGroupId: z.number().optional().or(z.null()),
  contentId: z.number().optional().or(z.null()),
  defaultContentId: z.number().optional().or(z.null()),
  areaId: z.number(),
  latitude: z.number().optional().or(z.null()),
  longitude: z.number().optional().or(z.null()),
  healthSequence: z.number().min(2),
});

export type FormSchemaType = z.infer<typeof formSchema>;

type Props = {
  buttonText: string;
  onSubmit: (values: FormSchemaType) => void;
  isLoading: boolean;
  defaultValues?: FormSchemaType;
};

export const PlayerMetaForm: React.FC<Props> = ({ buttonText, onSubmit, isLoading, defaultValues }) => {
  const activeFirm = useSelector(selectActiveFirm);
  const { t } = useTranslation();
  const [showMap, setShowMap] = useState(false);
  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues || {
      name: "",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      healthSequence: 2,
    },
  });

  const screenSizeId = form.watch("screenSizeId");
  const { data: screenSizeData, isLoading: isScreenSizeLoading } = useQuery({
    queryFn: () =>
      getScreenSize(
        {
          numberOfObjectsPerPage: 1000,
          pageNumber: 1,
        },
        {
          Firm_Id: String(activeFirm?.firmGuid),
        }
      ),
    queryKey: ["getScreenSize"],
  });

  const foundScreenSizeGuid = screenSizeData?.data.result.find((screenSize) => screenSize.intId === screenSizeId)?.id;

  return (
    <LoadingOverlay isLoading={isScreenSizeLoading}>
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-lg shadow-lg mb-8 mt-4">
        <div className="flex items-center space-x-4">
          <div className="bg-white p-3 rounded-full shadow-md">
            <MonitorIcon className="h-8 w-8 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">{t("player.configurePlayer")}</h1>
            <p className="text-white text-opacity-90">{t("player.configurePlayerDesc")}</p>
          </div>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          {/* Basic Information Section */}
          <Card className="border-l-4 border-l-blue-500 shadow-md">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-transparent pb-2">
              <CardTitle className="flex items-center text-blue-700">
                <MonitorIcon className="mr-2 h-5 w-5 text-blue-500" />
                {t("player.basicInformation")}
              </CardTitle>
              <CardDescription>{t("player.basicInformationDesc")}</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-blue-600">{t("common.name")}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t("common.name")}
                          {...field}
                          className="border-blue-200 focus-visible:ring-blue-500"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="screenSizeId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-blue-600">{t("entities.screenSize")}</FormLabel>
                      <FormControl>
                        <ScreenSizeSelect field={field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="timeZone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-blue-600">{t("entities.timeZone")}</FormLabel>
                      <FormControl>
                        <div>
                          <PlayerTimezoneSelect field={field} colorScheme="blue" />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="firmLicenceintId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-blue-600">{t("entities.firmLicence")}</FormLabel>
                      <FormControl>
                        <PlayerFirmLicenceSelect field={field} colorScheme="blue" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="healthSequence"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-blue-600">{t("player.healthSequence")}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t("player.healthSequence")}
                          {...field}
                          className="border-blue-200 focus-visible:ring-blue-500"
                          type="number"
                          onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Content Section */}
          <Card className="border-l-4 border-l-purple-500 shadow-md">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-transparent pb-2">
              <CardTitle className="flex items-center text-purple-700">
                <ImageIcon className="mr-2 h-5 w-5 text-purple-500" />
                {t("player.contentConfiguration")}
              </CardTitle>
              <CardDescription>{t("player.contentConfigurationDesc")}</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-6">
                  <div className="bg-purple-50 p-4 rounded-md border border-purple-100">
                    <h3 className="text-sm font-medium text-purple-700 mb-4 flex items-center">
                      <LayoutIcon className="mr-2 h-4 w-4" />
                      {t("content.defaultContent")}
                    </h3>
                    <PlayerMediaSelect
                      form={form}
                      visibleTypes={["content"]}
                      properties={{
                        contentId: "defaultContentId",
                      }}
                      label={t("content.defaultContent")}
                    />
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="bg-purple-50 p-4 rounded-md border border-purple-100">
                    <h3 className="text-sm font-medium text-purple-700 mb-4 flex items-center">
                      <ImageIcon className="mr-2 h-4 w-4" />
                      {t("common.media")}
                    </h3>
                    <PlayerMediaSelect
                      form={form}
                      visibleTypes={["channel", "contentGroup", "content"]}
                      properties={{
                        channelId: "channelId",
                        contentGroupId: "contentGroupId",
                        contentId: "contentId",
                      }}
                      defaultScreenSizeGuid={foundScreenSizeGuid}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Location Section */}
          <Card className="border-l-4 border-l-yellow-500 shadow-md">
            <CardHeader className="bg-gradient-to-r from-yellow-50 to-transparent pb-2">
              <CardTitle className="flex items-center text-yellow-700">
                <MapPinIcon className="mr-2 h-5 w-5 text-yellow-500" />
                {t("playerInfo.locationAndArea")}
              </CardTitle>
              <CardDescription>{t("playerInfo.locationAndAreaDesc")}</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-6">
                  <FormField
                    control={form.control}
                    name="areaId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-yellow-600">{t("entities.area")}</FormLabel>
                        <FormControl>
                          <div className="border border-yellow-200 rounded-md p-3 bg-yellow-50/30">
                            <NestedAreaSelector
                              selectedAreaIds={field.value ? [field.value] : []}
                              onSelectionChange={(selectedIds) => {
                                // For single selection, take the last selected area
                                const newAreaId = selectedIds.length > 0 ? selectedIds[selectedIds.length - 1] : null;
                                field.onChange(newAreaId);
                              }}
                              disabled={false}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-yellow-600 flex items-center">
                      <GlobeIcon className="mr-2 h-4 w-4" />
                      {t("player.geographicLocation")}
                    </h3>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowMap(!showMap)}
                      className="flex items-center gap-2 border-yellow-200 text-yellow-600 hover:bg-yellow-50"
                    >
                      <MapIcon className="h-4 w-4" />
                      {showMap ? t("common.hideMap") : t("common.showMap")}
                    </Button>
                  </div>

                  {showMap && (
                    <Card className="p-4 border-yellow-200 shadow-sm">
                      <LocationMapPicker
                        latitude={form.watch("latitude")}
                        longitude={form.watch("longitude")}
                        onLocationSelect={(lat: number, lng: number) => {
                          form.setValue("latitude", lat);
                          form.setValue("longitude", lng);
                        }}
                      />
                    </Card>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="latitude"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-yellow-600">{t("player.latitude")}</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.000001"
                              placeholder="e.g. 41.015137"
                              {...field}
                              value={field.value || ""}
                              onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                              className="border-yellow-200 focus-visible:ring-yellow-500"
                            />
                          </FormControl>
                          <FormDescription className="text-yellow-600/70">{t("player.latitudeHint")}</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="longitude"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-yellow-600">{t("player.longitude")}</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.000001"
                              placeholder="e.g. 28.979530"
                              {...field}
                              value={field.value || ""}
                              onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                              className="border-yellow-200 focus-visible:ring-yellow-500"
                            />
                          </FormControl>
                          <FormDescription className="text-yellow-600/70">{t("player.longitudeHint")}</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Hidden fields for form validation */}
          <div className="hidden">
            <FormField
              control={form.control}
              name="channelId"
              render={() => (
                <FormItem>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="contentGroupId"
              render={() => (
                <FormItem>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="contentId"
              render={() => (
                <FormItem>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex justify-end pt-4">
            <Button type="submit" disabled={isLoading} size="lg">
              {buttonText}
              {isLoading && <LoadingSmall />}
            </Button>
          </div>
        </form>
      </Form>
    </LoadingOverlay>
  );
};
